import { useCallback, useEffect, type RefObject } from 'react';

interface ResizeMessage {
  type: 'lincolns:form-resize';
  version: 1;
  height: number;
}

interface SubmittedMessage {
  type: 'lincolns:form-submitted';
  version: 1;
}

export const useIframeBridge = (
  containerRef: RefObject<HTMLElement | null>,
  parentOrigin: string,
) => {
  const postToParent = useCallback(
    (message: ResizeMessage | SubmittedMessage) => {
      if (window.parent === window) return;
      window.parent.postMessage(message, parentOrigin);
    },
    [parentOrigin],
  );

  const reportHeight = useCallback(() => {
    const containerHeight = Math.ceil(
      containerRef.current?.getBoundingClientRect().height ?? 0,
    );
    const documentHeight = Math.ceil(document.documentElement.scrollHeight);
    const height = Math.max(containerHeight, documentHeight);

    if (height > 0) {
      postToParent({ type: 'lincolns:form-resize', version: 1, height });
    }
  }, [containerRef, postToParent]);

  useEffect(() => {
    reportHeight();

    const observer = new ResizeObserver(reportHeight);
    if (containerRef.current) observer.observe(containerRef.current);

    window.addEventListener('load', reportHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('load', reportHeight);
    };
  }, [containerRef, reportHeight]);

  const notifySubmitted = useCallback(() => {
    postToParent({ type: 'lincolns:form-submitted', version: 1 });
    window.requestAnimationFrame(reportHeight);
  }, [postToParent, reportHeight]);

  return { notifySubmitted, reportHeight };
};
